import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  Stack,
  LinearProgress,
  Tooltip,
  Box,
} from '@mui/material';
import { Savings, CreditCard } from '@mui/icons-material';
import LoadingButton from './LoadingButton';
import { insightsApi, settlementsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatMoney } from '../utils/money';
import { formatDate } from '../utils/helpers';
import { groupSamePayments } from '../utils/upcoming';

// Outflows listed on the card before "and N more"
const OUTFLOWS_SHOWN = 4;

// One row in either card: name and detail on the left, amount on the right
const AmountRow = ({ divider, primary, secondary, amount, children }) => (
  <>
    {divider && <Divider component="li" />}
    <ListItem disableGutters sx={{ display: 'block' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <ListItemText primary={primary} secondary={secondary} sx={{ my: 0 }} />
        {amount}
      </Stack>
      {children}
    </ListItem>
  </>
);

/**
 * Dashboard: safe to spend until the next income, this cycle's spend per
 * credit card (and the best one to use today), and payments from friends who
 * owe you ("Mark as settled?").
 */
const InsightsCards = ({ sx }) => {
  const { showSuccess, showError } = useNotification();
  const [forecast, setForecast] = useState(null);
  const [cards, setCards] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [settling, setSettling] = useState(null);

  const load = useCallback(() => {
    insightsApi.forecast().then(r => setForecast(r.data)).catch(() => setForecast(null));
    insightsApi.cards().then(r => setCards(r.data)).catch(() => setCards(null));
    insightsApi.splitSuggestions().then(r => setSuggestions(r.data.suggestions || [])).catch(() => setSuggestions([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const settle = async (s) => {
    setSettling(s.transactionId);
    try {
      await settlementsApi.settle(s.friendEmail, s.settleAmount, s.transactionId);
      showSuccess(`Recorded ${formatMoney(s.settleAmount)} from ${s.friendName}`);
      setSuggestions(prev => prev.filter(x => x.transactionId !== s.transactionId));
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to record the payment');
    } finally {
      setSettling(null);
    }
  };

  const hasCash = forecast && forecast.cash.accounts.length > 0;
  // Payments to one payee on one day as one row (four SIPs on the 1st)
  const outGroups = forecast ? groupSamePayments(forecast.outflows, 'date') : [];
  const outTotal = forecast ? forecast.outflows.reduce((sum, o) => sum + o.amount, 0) : 0;
  const inTotal = forecast ? forecast.inflows.reduce((sum, o) => sum + o.amount, 0) : 0;
  const shownCards = cards ? cards.cards.slice(0, 4) : [];

  return (
    <Box sx={sx}>
      {suggestions.map(s => (
        <Alert
          key={s.transactionId}
          severity="success"
          sx={{ mb: 2 }}
          action={
            <LoadingButton color="inherit" size="small" loading={settling === s.transactionId} onClick={() => settle(s)}>
              Mark as settled
            </LoadingButton>
          }
        >
          {s.friendName} paid you {formatMoney(s.received)} on {formatDate(s.date)}, and owes you {formatMoney(s.owed)}.
          {s.exact ? ' That settles it.' : ` Record ${formatMoney(s.settleAmount)} as their payment?`}
        </Alert>
      ))}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardHeader
              avatar={<Savings color="primary" />}
              title="Safe to spend"
              subheader={!forecast ? 'Until your next income'
                : forecast.payday ? `Until your ${forecast.payday.name} credit on ${formatDate(forecast.payday.date)}`
                : `Next 30 days, until ${formatDate(forecast.horizon)}`}
            />
            <CardContent sx={{ pt: 1 }}>
              {!forecast ? null : !hasCash ? (
                <Alert severity="info">
                  Needs a recent bank balance: forward bank alerts that show your available balance.
                </Alert>
              ) : (
                <>
                  <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
                    <Typography variant="h4" color={forecast.left < 0 ? 'error.main' : 'text.primary'}>
                      {formatMoney(forecast.left)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      about {formatMoney(forecast.perDay)} a day
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatMoney(forecast.cash.total)} in your bank accounts
                    {outTotal > 0 && `, minus ${formatMoney(outTotal)} due before then`}
                    {inTotal > 0 && `, plus ${formatMoney(inTotal)} expected in`}
                  </Typography>
                  {outGroups.length > 0 && (
                    <List disablePadding sx={{ mt: 1 }}>
                      {outGroups.slice(0, OUTFLOWS_SHOWN).map((o, i) => (
                        <AmountRow
                          key={`${o.name}-${o.date}-${i}`}
                          divider={i > 0}
                          primary={`${o.name}${o.count > 1 ? ` ×${o.count}` : ''}`}
                          secondary={o.date ? `Due ${formatDate(o.date)}${o.dueEstimated ? ' (estimated)' : ''}` : null}
                          amount={
                            <Tooltip title={o.estimated ? 'Estimated from the last bill: the amount is in the statement PDF' : ''}>
                              <Typography variant="body1">{o.estimated ? '≈ ' : ''}{formatMoney(o.amount)}</Typography>
                            </Tooltip>
                          }
                        />
                      ))}
                    </List>
                  )}
                  {outGroups.length > OUTFLOWS_SHOWN && (
                    <Typography variant="caption" color="text.secondary">and {outGroups.length - OUTFLOWS_SHOWN} more</Typography>
                  )}
                  {forecast.notes.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>{forecast.notes[0]}</Alert>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardHeader
              avatar={<CreditCard color="primary" />}
              title="Cards this cycle"
              subheader={cards && cards.bestToday
                ? `Best to use today: ${cards.bestToday.name} (${cards.bestToday.daysToStatement} days to its statement)`
                : 'Spent on each card this billing cycle'}
            />
            <CardContent sx={{ pt: 1 }}>
              {cards && shownCards.length === 0 && (
                <Alert severity="info">No credit card used in the last two months.</Alert>
              )}
              <List disablePadding>
                {shownCards.map((card, index) => (
                  <AmountRow
                    key={card.id}
                    divider={index > 0}
                    primary={card.name}
                    secondary={`${card.purchases} purchase${card.purchases === 1 ? '' : 's'} since ${formatDate(card.cycleStart)}`
                      + (!card.statementDay ? ' (statement date not known yet)' : '')
                      + (card.nextStatement ? ` · statement on ${formatDate(card.nextStatement)}` : '')
                      + (card.limit ? ` · ${card.limitUsed}% of the ${formatMoney(card.limit)} limit` : '')}
                    amount={<Typography variant="body1">{formatMoney(card.spent)}</Typography>}
                  >
                    {card.limit && (
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, card.limitUsed)}
                        color={card.limitUsed > 80 ? 'error' : card.limitUsed > 50 ? 'warning' : 'primary'}
                        sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                      />
                    )}
                  </AmountRow>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InsightsCards;
